import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { DropdownActionItem, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import { ConfirmDropdownItem } from '../../../configurator-components/confirmTwice';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { getComposerActionDefinition } from '../../../configurator-components/composer-editors/attributes/composerActionCatalog';
import { ComposerOptionActionNode, ComposerOptionActionsType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerNodeValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';
import { ComposerActionGlyph } from '../../../configurator-components/composer-editors/attributes/composerActionGlyph';
import { ComposerActionTypeMenu } from '../../../configurator-components/composer-editors/attributes/composerActionEditor';
import {
    ComposerBlock,
    ComposerBlockSelectMode,
    ComposerBlockSubtitle,
    ComposerBlockTitle
} from '../../../configurator-components/composer-editors/composerBlock';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';

interface ComposerActionTreeElementProps {
    action: ComposerOptionActionNode;
    findMesh: (meshId: string) => BABYLON.AbstractMesh | undefined;
    findMaterial: (materialId: string) => BABYLON.Material | undefined;
    findCamera: (cameraId: string) => BABYLON.Camera | undefined;
    findPrintingMethod?: (printingMethodId: string) => { name?: string } | undefined;
    open: boolean;
    onToggleOpen: () => void;
    selected?: boolean;
    /** La casella o ⇧ / ⌘ sulla testata. */
    onSelect?: (mode: ComposerBlockSelectMode, action: ComposerOptionActionNode) => void;
    /** Il tipo si cambia dal titolo; senza, il titolo è solo il nome (un'azione condivisa da più opzioni). */
    onTypeChange?: (action: ComposerOptionActionNode, type: ComposerOptionActionsType) => void;
    onPlay?: (action: ComposerOptionActionNode) => void;
    onVisibilityClick?: (action: ComposerOptionActionNode) => void;
    onClone?: (action: ComposerOptionActionNode) => void;
    /** Copy to apre l'elenco delle opzioni accanto al «…». */
    onCopyTo?: (action: ComposerOptionActionNode, anchor: HTMLElement | null) => void;
    onRemove?: (action: ComposerOptionActionNode) => void;
    onMoveUp?: (action: ComposerOptionActionNode) => void;
    onMoveDown?: (action: ComposerOptionActionNode) => void;
    /** La linguetta: il trascinamento lo conduce chi ospita le card (`startPointerReorder`). */
    onGripPointerDown?: (event: React.PointerEvent<HTMLButtonElement>, action: ComposerOptionActionNode) => void;
    editor?: React.ReactNode;
    /** Al posto del sottotitolo: «Mixed values» di un'azione condivisa da più opzioni. */
    caption?: string;
}

// LA CARD DELL'AZIONE (prototipo V3, Rules; utente 2026-10-20): il tipo nel titolo, che si cambia da lì, e sotto su
// che cosa agisce — il nome, o «Multiple» se sono più d'uno. Play e occhio al passaggio; Clone, Copy to e Remove nel «…».
const UNDEFINED_PLACEHOLDER = '—';

export const ComposerActionTreeElement: FC<ComposerActionTreeElementProps> = ({
    action,
    findMesh,
    findMaterial,
    findCamera,
    findPrintingMethod,
    open,
    onToggleOpen,
    selected = false,
    onSelect,
    onTypeChange,
    onPlay,
    onVisibilityClick,
    onClone,
    onCopyTo,
    onRemove,
    onMoveUp,
    onMoveDown,
    onGripPointerDown,
    editor,
    caption
}) => {
    const enabled = action.enabled !== false;
    const menuAnchor = React.useRef<HTMLSpanElement>(null);
    const names = (find: (id: string) => { name?: string } | undefined, value: string) =>
        value
            .split(/[;,]/)
            .filter(Boolean)
            .map((id) => find(id.trim())?.name ?? id.trim());
    // L'INPUT DELL'AZIONE, O IL SUO VALORE dove un input non c'è (la camera, il metodo di stampa).
    const inputs = React.useMemo(() => {
        switch (action.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
            case ComposerOptionActionsType.HIDE_OBJECTS:
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return names(findMesh, action.selector);
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return names(findMaterial, action.selector);
            case ComposerOptionActionsType.MOVE_CAMERA:
                return names(findCamera, action.selector || action.value);
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return names(findCamera, action.value);
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return action.value ? [findPrintingMethod?.(action.value)?.name || action.value] : [];
            default:
                return [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, findCamera, findMaterial, findMesh, findPrintingMethod]);

    const definition = getComposerActionDefinition(action.type);
    const validationMessages = getComposerNodeValidationMessages(action);
    const subtitle =
        caption ??
        (action.type === ComposerOptionActionsType.RESET_CAMERA_PIVOT
            ? T._('Back to the center', 'Admin')
            : inputs.length > 1
              ? T._('Multiple', 'Admin')
              : (inputs[0] ?? UNDEFINED_PLACEHOLDER));

    const menu = [
        [
            onClone && (
                <DropdownActionItem key='clone' icon={<DuplicateIcon />} onClick={() => onClone(action)}>
                    {T._('Clone', 'Admin')}
                </DropdownActionItem>
            ),
            onCopyTo && (
                <DropdownActionItem key='copy' icon={<CopyToIcon />} onClick={() => onCopyTo(action, menuAnchor.current)}>
                    {T._('Copy to…', 'Admin')}
                </DropdownActionItem>
            )
        ],
        [
            onRemove && (
                <ConfirmDropdownItem
                    key='remove'
                    icon={
                        <DropdownItemIcon>
                            <TrashIcon />
                        </DropdownItemIcon>
                    }
                    onConfirm={() => onRemove(action)}
                >
                    {T._('Remove', 'Admin')}
                </ConfirmDropdownItem>
            )
        ]
    ];
    const hasMenu = !!(onClone || onCopyTo || onRemove);

    return (
        <ComposerBlock
            id={action.id}
            open={open}
            onToggleOpen={onToggleOpen}
            selected={selected}
            onSelect={onSelect ? (mode) => onSelect(mode, action) : undefined}
            off={!enabled}
            glyph={<ComposerActionGlyph icon={definition.icon} />}
            onGripPointerDown={onGripPointerDown ? (event) => onGripPointerDown(event, action) : undefined}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && event.altKey) {
                    event.preventDefault();
                    onMoveUp?.(action);
                }
                if (event.key === 'ArrowDown' && event.altKey) {
                    event.preventDefault();
                    onMoveDown?.(action);
                }
            }}
            title={
                <>
                    <TitleLine>
                        {onTypeChange ? (
                            <ComposerActionTypeMenu type={action.type} open={open} onChange={(type) => onTypeChange(action, type)} />
                        ) : (
                            <ComposerBlockTitle $off={!enabled} $open={open}>
                                {definition.name}
                            </ComposerBlockTitle>
                        )}
                        {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
                    </TitleLine>
                    <ZTooltip title={inputs.length > 1 ? inputs.join(', ') : subtitle} position='top' isOverflowEnabled={inputs.length <= 1}>
                        <ComposerBlockSubtitle>{subtitle}</ComposerBlockSubtitle>
                    </ZTooltip>
                </>
            }
            commands={
                <>
                    {onPlay && (
                        <ZTooltip title={T._('Run this action', 'Admin')}>
                            <ActionButton aria-label={T._('Run this action', 'Admin')} onClick={() => onPlay(action)}>
                                <PlayIcon />
                            </ActionButton>
                        </ZTooltip>
                    )}
                    {onVisibilityClick && (
                        <ZTooltip title={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}>
                            <ActionButton
                                $off={!enabled}
                                aria-label={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}
                                aria-pressed={!enabled}
                                data-persistent={enabled ? undefined : 'true'}
                                onClick={() => onVisibilityClick(action)}
                            >
                                {enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </ActionButton>
                        </ZTooltip>
                    )}
                    {hasMenu && <MenuAnchor ref={menuAnchor} aria-hidden='true' />}
                </>
            }
            menu={hasMenu ? menu : undefined}
        >
            {editor}
        </ComposerBlock>
    );
};

const TitleLine = styled.span`
    display: flex;
    max-width: 100%;
    min-width: 0;
    align-items: center;
    gap: 4px;
`;

// Il punto a cui si aggancia l'elenco di Copy to: accanto al «…», che col suo menu chiuso non c'è più.
const MenuAnchor = styled.span`
    width: 0;
    height: 24px;
`;

// LA CARD CHE NASCE (prototipo, «New rule»): vuota e aperta, col titolo che chiede il tipo. Il menu non si apre da
// solo: la card c'è, e si sceglie quando si vuole. Remove nel «…» la lascia perdere.
export const ComposerActionDraftCard: FC<{ onChoose: (type: ComposerOptionActionsType) => void; onDiscard: () => void }> = ({ onChoose, onDiscard }) => (
    <ComposerBlock
        id='composer-action-draft'
        open
        onToggleOpen={() => undefined}
        glyph={<AddIcon />}
        title={<ComposerActionTypeMenu onChange={onChoose} />}
        menu={[
            [
                <DropdownActionItem key='discard' icon={<TrashIcon />} danger onClick={onDiscard}>
                    {T._('Discard', 'Admin')}
                </DropdownActionItem>
            ]
        ]}
    >
        <DraftHint>{T._('Choose what this action does from its title.', 'Admin')}</DraftHint>
    </ComposerBlock>
);

const DraftHint = styled.p`
    margin: 0;
    color: var(--editor-faint, #767a7f);
    font-size: 11px;
    line-height: 16px;
`;
